import { useState, useEffect, useCallback } from 'react';
import { Order } from '@/shared/types';
import { ordersApi } from '@/shared/api/ordersApi';

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string | null;
  refreshOrders: () => Promise<void>;
  fetchOrderById: (id: string) => Promise<Order | null>;
  updateOrderDelivery: (
    orderId: string,
    payload: Parameters<typeof ordersApi.updateOrderDelivery>[1]
  ) => Promise<Order>;
  updateOrderItems: (
    orderId: string,
    items: Parameters<typeof ordersApi.updateOrderItems>[1]
  ) => Promise<Order>;
  addOrderItems: (
    orderId: string,
    items: Parameters<typeof ordersApi.addOrderItems>[1]
  ) => Promise<Order>;
  updateOrderPaymentMethod: (
    orderId: string,
    paymentMethod: "yookassa" | "cash"
  ) => Promise<Order>;
  cancelOrder: (orderId: string) => Promise<Order>;
  payOrder: (
    orderId: string
  ) => Promise<{ paymentUrl: string; amount: number; reused: boolean }>;
}

export const useOrders = (): UseOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await ordersApi.getOrders();
      setOrders(
        data.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        )
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Ошибка загрузки заказов'
      );
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrderById = useCallback(async (id: string) => {
    return ordersApi.getOrderById(id);
  }, []);

  const updateOrderInList = useCallback((updated: Order) => {
    setOrders((prev) =>
      prev.map((order) => (order.id === updated.id ? updated : order))
    );
  }, []);

  const updateOrderDelivery = useCallback(
    async (
      orderId: string,
      payload: Parameters<typeof ordersApi.updateOrderDelivery>[1]
    ) => {
      const updated = await ordersApi.updateOrderDelivery(orderId, payload);
      updateOrderInList(updated);
      return updated;
    },
    [updateOrderInList]
  );

  const updateOrderItems = useCallback(
    async (
      orderId: string,
      items: Parameters<typeof ordersApi.updateOrderItems>[1]
    ) => {
      const updated = await ordersApi.updateOrderItems(orderId, items);
      updateOrderInList(updated);
      return updated;
    },
    [updateOrderInList]
  );

  const addOrderItems = useCallback(
    async (
      orderId: string,
      items: Parameters<typeof ordersApi.addOrderItems>[1]
    ) => {
      const updated = await ordersApi.addOrderItems(orderId, items);
      updateOrderInList(updated);
      return updated;
    },
    [updateOrderInList]
  );

  const updateOrderPaymentMethod = useCallback(
    async (orderId: string, paymentMethod: "yookassa" | "cash") => {
      const updated = await ordersApi.updateOrderPaymentMethod(
        orderId,
        paymentMethod
      );
      updateOrderInList(updated);
      return updated;
    },
    [updateOrderInList]
  );

  const cancelOrder = useCallback(
    async (orderId: string) => {
      const updated = await ordersApi.cancelOrder(orderId);
      updateOrderInList(updated);
      return updated;
    },
    [updateOrderInList]
  );

  const payOrder = useCallback(async (orderId: string) => {
    return ordersApi.payOrder(orderId);
  }, []);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    refreshOrders: fetchOrders,
    fetchOrderById,
    updateOrderDelivery,
    updateOrderItems,
    addOrderItems,
    updateOrderPaymentMethod,
    cancelOrder,
    payOrder,
  };
};
